import { useEffect, useMemo, useCallback, useState, type CSSProperties } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { Button, Layout, Spin } from 'antd'
import {
  PlayCircleOutlined,
  PauseCircleOutlined,
  HeartOutlined,
  HeartFilled,
  LeftOutlined,
  CustomerServiceOutlined,
} from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import 'dayjs/locale/en'
import 'dayjs/locale/fr'
import 'dayjs/locale/uk'

import { useAppDispatch, useAppSelector } from '../../utils/redux/store'
import { attemptGetPlaylist, Song } from '../../utils/redux/playlistSlice'
import {
  attemptAddPlaylistToFavs,
  attemptDeletePlaylistFromFavs,
  attemptGetFavs,
} from '../../utils/redux/favsSlice'
import { useAudioPlayer } from '../../contexts/AudioPlayerContext'
import { apiBaseURL } from '../../utils/api'

import './Playlist.scss'
import SearchInput from '../../components/SearchInput/SearchInput'

const { Content } = Layout

function getDayjsLocale(lng: string) {
  const short = String(lng || 'en').split('-')[0]
  if (short === 'fr') return 'fr'
  if (short === 'uk') return 'uk'
  return 'en'
}

type ThemeMode = 'light' | 'dark'
type UploadedDestination = 'styles' | 'moods' | 'daytimes' | 'businesses'

type ChipItem = {
  key: string
  id: string
  label: string
  uploadedDestination: UploadedDestination
  image?: string
}

function detectThemeMode(): ThemeMode {
  if (typeof document === 'undefined') return 'dark'

  const html = document.documentElement
  const body = document.body

  const attrs = [
    body.getAttribute('data-theme'),
    html.getAttribute('data-theme'),
    body.getAttribute('theme'),
    html.getAttribute('theme'),
  ]
    .filter(Boolean)
    .map((v) => String(v).toLowerCase())

  let storageTheme = ''
  try {
    storageTheme = String(localStorage.getItem('theme') || '').toLowerCase()
  } catch {
    // noop
  }

  const isLightAttr = attrs.some((v) => v.includes('light'))
  const isLightClass =
    body.classList.contains('light') ||
    html.classList.contains('light') ||
    body.classList.contains('theme-light') ||
    html.classList.contains('theme-light')

  const isLightStorage = storageTheme.includes('light')

  return isLightAttr || isLightClass || isLightStorage ? 'light' : 'dark'
}

function isMongoId(s: string) {
  return /^[a-f\d]{24}$/i.test(String(s))
}

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

export default function Playlist() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useAppDispatch()
  const { t, i18n } = useTranslation('playlist')

  const langShort = useMemo(
    () => String(i18n.language || 'en').split('-')[0],
    [i18n.language],
  )
  const isUk = langShort === 'uk'

  const [themeMode, setThemeMode] = useState<ThemeMode>('dark')

  const playlist = useAppSelector((state) =>
    id ? state.playlist.playlists[id] : undefined,
  )

  const favPlaylists = useAppSelector((state) => state.favs.playlists)

  const moodsDict = useAppSelector(
    (s: any) => s.playlist?.moods ?? s.playlist?.categories?.moods ?? [],
  )
  const stylesDict = useAppSelector(
    (s: any) => s.playlist?.styles ?? s.playlist?.categories?.styles ?? [],
  )
  const daytimesDict = useAppSelector(
    (s: any) => s.playlist?.daytimes ?? s.playlist?.categories?.daytimes ?? [],
  )
  const businessesDict = useAppSelector(
    (s: any) => s.playlist?.businesses ?? s.playlist?.categories?.businesses ?? [],
  )

  const { currentSong, isPlaying, isLoading, playSong, togglePlayPause } =
    useAudioPlayer()

  useEffect(() => {
    dayjs.locale(getDayjsLocale(i18n.language))
  }, [i18n.language])

  useEffect(() => {
    if (id) dispatch(attemptGetPlaylist(id))
  }, [id, dispatch])

  useEffect(() => {
    dispatch(attemptGetFavs())
  }, [dispatch])

  useEffect(() => {
    const apply = () => setThemeMode(detectThemeMode())
    apply()

    const html = document.documentElement
    const body = document.body

    const observer = new MutationObserver(() => apply())
    observer.observe(html, {
      attributes: true,
      attributeFilter: ['class', 'data-theme', 'theme'],
    })
    observer.observe(body, {
      attributes: true,
      attributeFilter: ['class', 'data-theme', 'theme'],
    })

    return () => observer.disconnect()
  }, [])

  const isFavorite = favPlaylists?.some((fav) => String(fav._id) === String(id)) || false

  const asText = useCallback(
    (val: any): string => {
      if (val == null) return ''
      if (typeof val === 'string' || typeof val === 'number') return String(val)
      if (Array.isArray(val)) return val.map(asText).filter(Boolean).join(' ')

      if (typeof val === 'object') {
        const lang = (i18n.language || 'en').split('-')[0]
        const pick = val?.[lang] ?? val?.en ?? val?.ru ?? val?.fr ?? val?.uk
        if (pick != null) return asText(pick)
        const firstStr = Object.values(val).find((v) => typeof v === 'string')
        return firstStr ? String(firstStr) : ''
      }

      return ''
    },
    [i18n.language],
  )

  /**
   * Берём перевод единицы времени и удаляем дублирующееся число спереди, если оно есть.
   * Пример:
   *   t('time.hour',{count:3}) -> "3 hours"  => "hours"
   *   t('time.hour',{count:3}) -> "hours"    => "hours"
   *   t('time.hour',{count:3}) -> "3год"     => "год"
   */
  const unitLabel = useCallback(
    (count: number, unit: 'minute' | 'hour' | 'day' | 'month' | 'year') => {
      const raw = String(
        t(`time.${unit}`, {
          count,
          defaultValue: unit + (count === 1 ? '' : 's'),
        }) ?? '',
      )
        .replace(/\u00a0/g, ' ')
        .trim()

      if (!raw) return unit + (count === 1 ? '' : 's')

      const countStr = String(count)
      const withoutCount = raw
        .replace(new RegExp(`^${escapeRegExp(countStr)}\\s*`), '')
        .trim()

      return withoutCount || raw
    },
    [t],
  )

  const countWithUnit = useCallback(
    (count: number, unit: 'minute' | 'hour' | 'day' | 'month' | 'year') => {
      return `${count} ${unitLabel(count, unit)}`.trim()
    },
    [unitLabel],
  )

  /**
   * Длительность:
   * - < 60 мин: "42min"
   * - >= 60 мин: "3h 30min"
   */
  const shortUnit = useCallback(
    (unit: 'minute' | 'hour' | 'day' | 'month' | 'year') =>
      t(`timeShort.${unit}`, {
        defaultValue:
          unit === 'minute'
            ? 'min'
            : unit === 'hour'
              ? 'h'
              : unit === 'day'
                ? 'd'
                : unit === 'month'
                  ? 'mo'
                  : 'y',
      }),
    [t],
  )

  const formatTotalDurationFigma = useCallback(
    (seconds?: number) => {
      if (!seconds || seconds <= 0) return `0${shortUnit('minute')}`

      const totalMinutes = Math.max(1, Math.round(seconds / 60))
      const hours = Math.floor(totalMinutes / 60)
      const minutes = totalMinutes % 60

      if (hours <= 0) {
        return `${totalMinutes}${shortUnit('minute')}`
      }

      if (minutes === 0) {
        return `${hours}${shortUnit('hour')}`
      }

      return `${hours}${shortUnit('hour')} ${minutes}${shortUnit('minute')}`
    },
    [shortUnit],
  )

  const formatUpdatedAgoFigma = useCallback(
    (date?: string | Date) => {
      if (!date) return ''

      const d = dayjs(date)
      if (!d.isValid()) return ''

      const now = dayjs()
      const minutes = now.diff(d, 'minute')

      let value = ''
      if (minutes < 60) {
        const m = Math.max(1, minutes)
        value = countWithUnit(m, 'minute')
      } else {
        const hours = now.diff(d, 'hour')
        if (hours < 24) {
          const h = Math.max(1, hours)
          value = countWithUnit(h, 'hour')
        } else {
          const days = now.diff(d, 'day')
          if (days < 30) {
            const dd = Math.max(1, days)
            value = countWithUnit(dd, 'day')
          } else {
            const months = now.diff(d, 'month')
            if (months < 12) {
              const mm = Math.max(1, months)
              value = countWithUnit(mm, 'month')
            } else {
              const years = now.diff(d, 'year')
              const yy = Math.max(1, years)
              value = countWithUnit(yy, 'year')
            }
          }
        }
      }

      return t('time.ago', {
        value,
        defaultValue: `${value} ago`,
      })
    },
    [countWithUnit, t],
  )

  const titleText = useMemo(() => asText(playlist?.title), [playlist?.title, asText])
  const descText = useMemo(
    () => asText((playlist as any)?.description),
    [(playlist as any)?.description, asText],
  )

  const coverUrl = useMemo(() => {
    if (!playlist?.image) return ''
    return `${apiBaseURL}/uploaded/playlists/${playlist.image}`
  }, [playlist?.image])

  const songsCount = playlist?.songsCount ?? playlist?.songs?.length ?? 0

  const songsCountText = useMemo(() => {
    return t('meta.songsCount', {
      count: songsCount,
      defaultValue: `${songsCount} songs`,
    })
  }, [songsCount, t])

  const totalDurationText = useMemo(() => {
    return formatTotalDurationFigma((playlist as any)?.totalDuration)
  }, [formatTotalDurationFigma, (playlist as any)?.totalDuration])

  const updatedAgoText = useMemo(() => {
    return formatUpdatedAgoFigma((playlist as any)?.updatedAt)
  }, [formatUpdatedAgoFigma, (playlist as any)?.updatedAt])

  const idToTitle = useMemo(() => {
    const map = new Map<string, string>()
    const put = (arr: any[]) => {
      ;(arr || []).forEach((it) => {
        const key = String(it?._id ?? '')
        const val = asText(it?.title ?? it?.name)
        if (key && val) map.set(key, val)
      })
    }
    put(moodsDict)
    put(stylesDict)
    put(daytimesDict)
    put(businessesDict)
    return map
  }, [moodsDict, stylesDict, daytimesDict, businessesDict, asText])

  const chips: ChipItem[] = useMemo(() => {
    const p: any = playlist || {}

    const sources: Array<{ raw: any; destination: UploadedDestination }> = [
      { raw: p.styles, destination: 'styles' },
      { raw: p.moods, destination: 'moods' },
      { raw: p.daytimes, destination: 'daytimes' },
      { raw: p.businesses, destination: 'businesses' },
    ]

    const out: ChipItem[] = []
    const seen = new Set<string>()

    for (const src of sources) {
      const list = Array.isArray(src.raw) ? src.raw : src.raw ? [src.raw] : []

      for (const item of list) {
        const rawId =
          typeof item === 'object'
            ? String(item?._id ?? item?.id ?? '')
            : String(item ?? '')

        const id = rawId.trim()
        if (!id) continue

        const objLabel = typeof item === 'object' ? asText(item?.title ?? item?.name) : ''
        const dictLabel = idToTitle.get(id) ?? ''
        let label = (objLabel || dictLabel || id).trim()

        if (isMongoId(label) && !objLabel && !dictLabel) continue
        if (!label) continue

        const key = `${src.destination}:${id}`
        if (seen.has(key)) continue
        seen.add(key)

        out.push({
          key,
          id,
          label,
          uploadedDestination: src.destination,
          image: typeof item === 'object' ? (item as any)?.image : undefined,
        })

        if (out.length >= 6) return out
      }
    }

    return out
  }, [playlist, asText, idToTitle])

  const stateAny = (location.state ?? {}) as any
  const fromCategoryId = String(stateAny?.fromCategoryId ?? '')
  const fromCategoryType = String(stateAny?.fromCategoryType ?? '')

  const allSongs = useMemo(() => (playlist?.songs ?? []) as Song[], [playlist?.songs])

  const formatDurationSong = (seconds?: number) => {
    if (!seconds) return '0:00'
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const getSongImage = (song: any) =>
    song?.imageUrl ||
    (song?.image ? `${apiBaseURL}/uploaded/songs/${song.image}` : '') ||
    ''

  const handlePlaySong = (song: Song) => {
    if (!allSongs.length) return

    if (currentSong?._id === song._id) {
      togglePlayPause()
      return
    }

    playSong(song, allSongs)
  }

  const handleToggleFavorite = async () => {
    if (!id) return

    try {
      if (isFavorite) await dispatch(attemptDeletePlaylistFromFavs(id)).unwrap()
      else await dispatch(attemptAddPlaylistToFavs(id)).unwrap()
      dispatch(attemptGetFavs())
    } catch {
      // noop
    }
  }

  const handleOpenCategory = useCallback(
    (chip: ChipItem) => {
      if (fromCategoryId && fromCategoryType) {
        if (fromCategoryId === chip.id && fromCategoryType === chip.uploadedDestination) {
          navigate(-1)
          return
        }
      }

      navigate(`/playlist-category/${chip.uploadedDestination}/${chip.id}`, {
        state: {
          title: chip.label,
          _id: chip.id,
          image: chip.image,
          uploadedDestination: chip.uploadedDestination,
          fromPlaylistId: id,
        },
      })
    },
    [fromCategoryId, fromCategoryType, navigate, id],
  )

  if (!playlist) {
    return (
      <div className="pl-loading">
        <Spin size="large" />
      </div>
    )
  }

  return (
    <Content
      className={`pl ${themeMode === 'light' ? 'pl--light' : 'pl--dark'}`}
      style={
        {
          ['--pl-bg' as any]: coverUrl ? `url(${coverUrl})` : 'none',
        } as CSSProperties
      }
    >
      <div className="pl-top">
        <SearchInput />
      </div>

      <div className="pl-wrap">
        <aside className="pl-left">
          <div className="pl-card">
            <div className="pl-coverWrap">
              <button
                type="button"
                className="pl-back"
                onClick={() => navigate(-1)}
                aria-label={t('actions.back', { defaultValue: 'Back' })}
              >
                <LeftOutlined />
              </button>

              <img className="pl-cover" src={coverUrl} alt={titleText} />

              <button
                type="button"
                className="pl-fav"
                onClick={handleToggleFavorite}
                aria-label={t('actions.favorite', { defaultValue: 'Favorite' })}
                title={t('actions.favorite', { defaultValue: 'Favorite' })}
              >
                {isFavorite ? <HeartFilled /> : <HeartOutlined />}
              </button>
            </div>

            <div className="pl-title">{titleText}</div>

            <div className={`pl-meta ${isUk ? 'pl-meta--uk' : ''}`}>
              <span>{songsCountText}</span>
              <span className="pl-dot">|</span>
              <span>{totalDurationText}</span>
              <span className="pl-dot">|</span>
              <span>
                {t('meta.updated', { defaultValue: 'Updated' })} {updatedAgoText || ''}
              </span>
            </div>

            {descText && <div className="pl-desc">{descText}</div>}

            {chips.length > 0 && (
              <div className="pl-chips">
                {chips.map((c, idx) => {
                  const isFromCategory =
                    fromCategoryId === c.id && fromCategoryType === c.uploadedDestination
                  const isDefaultPrimary = !fromCategoryId && idx === 0

                  return (
                    <button
                      key={c.key}
                      type="button"
                      onClick={() => handleOpenCategory(c)}
                      className={`pl-chip ${isDefaultPrimary || isFromCategory ? 'is-primary' : ''} ${isFromCategory ? 'is-active' : ''}`}
                    >
                      {c.label}
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        </aside>

        <section className="pl-right">
          <div className="pl-list">
            {allSongs.map((song: any) => {
              const isCurrent = currentSong?._id === song._id
              const isCurrentPlaying = isCurrent && isPlaying
              const img = getSongImage(song)

              return (
                <div
                  key={song._id}
                  className={`pl-row ${isCurrent ? 'is-current' : ''}`}
                  onClick={() => handlePlaySong(song)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      handlePlaySong(song)
                    }
                  }}
                >
                  <div className="pl-row__left">
                    <div className="pl-row__imgWrap">
                      {img ? (
                        <img className="pl-row__img" src={img} alt={song.title} />
                      ) : (
                        <div className="pl-row__img pl-row__img--empty">
                          <CustomerServiceOutlined />
                        </div>
                      )}

                      <div className="pl-row__overlay">
                        <Button
                          type="text"
                          className="pl-row__playBtn"
                          icon={
                            isCurrentPlaying ? (
                              <PauseCircleOutlined />
                            ) : (
                              <PlayCircleOutlined />
                            )
                          }
                          onClick={(e) => {
                            e.stopPropagation()
                            handlePlaySong(song)
                          }}
                          loading={isLoading && isCurrent}
                        />
                      </div>
                    </div>

                    <div className="pl-row__text">
                      <div className="pl-row__title">{song.title}</div>
                      <div className="pl-row__sub">{song.artist}</div>
                    </div>
                  </div>

                  <div className="pl-row__right">
                    <div className="pl-row__time">
                      {formatDurationSong(song.duration)}
                    </div>

                    <button
                      type="button"
                      className="pl-row__fav"
                      aria-label={t('actions.like', { defaultValue: 'Like' })}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <HeartOutlined />
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        </section>
      </div>
    </Content>
  )
}
